import React, { useState, useEffect, useCallback } from 'react';
import baseapi from '../../../services/baseapi';
import { Search, Info, ChevronRight, ChevronDown, ChevronLeft, ChevronsLeft, ChevronsRight, RefreshCw, FileText, Clock, AlignLeft, Terminal } from 'lucide-react';
import { CustomDropdown } from '../../../components/CustomDropdown ';
import { ItemsPerPageDropdown } from '../../../components/ItemsPerPageDropdown ';
import { useNavigate } from 'react-router-dom';
import { K8sPodShellModal } from './K8sPodShellModal';
import { K8sPodLogsModal } from './K8sPodLogsModal';
import { showError } from '../../../utils/toast';

interface PodsTabProps {
  agentId: string;
  clientId: string;
}

const fmtBytes = (bytes: number | null | undefined) => {
  if (bytes == null) return '—';
  if (bytes === 0) return '0 B';
  const k = 1024, sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const fmtCpu = (cpu: number | null | undefined) => {
  if (cpu == null) return '—';
  return `${cpu}m`;
};

const PodsTab: React.FC<PodsTabProps> = ({ agentId, clientId }) => {
  const [loading, setLoading] = useState(true);
  const [pods, setPods] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [expandedPods, setExpandedPods] = useState<Set<string>>(new Set());
  const [describePod, setDescribePod] = useState<any>(null);
  const [shellConfig, setShellConfig] = useState<{ namespace: string, podName: string, containerName: string } | null>(null);
  const [logsConfig, setLogsConfig] = useState<{ podUid: string; podName: string; containerName: string } | null>(null);
  const navigate = useNavigate();


  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(handler);
  }, [search]);

  // Reset page when search or filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, statusFilter]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const skip = (currentPage - 1) * itemsPerPage;

      const queryObj: any = {};

      if (debouncedSearch && statusFilter && statusFilter !== 'problem') {
        queryObj.$and = [
          {
            $or: [
              { pod_name: { "$ilike": `%${debouncedSearch}%` } },
              { name: { "$ilike": `%${debouncedSearch}%` } },
              { namespace: { "$ilike": `%${debouncedSearch}%` } },
              { node_name: { "$ilike": `%${debouncedSearch}%` } }
            ]
          },
          {
            $or: [
              { status: statusFilter },
              { phase: statusFilter }
            ]
          }
        ];
      } else if (debouncedSearch) {
        queryObj.$or = [
          { pod_name: { "$ilike": `%${debouncedSearch}%` } },
          { name: { "$ilike": `%${debouncedSearch}%` } },
          { namespace: { "$ilike": `%${debouncedSearch}%` } },
          { node_name: { "$ilike": `%${debouncedSearch}%` } }
        ];
      } else if (statusFilter && statusFilter !== 'problem') {
        queryObj.$or = [
          { status: statusFilter },
          { phase: statusFilter }
        ];
      }

      const clusterName = new URLSearchParams(window.location.search).get('cluster_name');
      const params: any = {
        agent_id: agentId,
        client_id: clientId,
        skip,
        limit: itemsPerPage
      };

      if (clusterName) {
        params.cluster_name = clusterName;
      }

      if (Object.keys(queryObj).length > 0) {
        params.query = JSON.stringify(queryObj);
      }

      const res = await baseapi.get('/k8s_dashboard/pods', { params });
      if (res.data?.status) {
        const fetchedPods = res.data.data?.pods || res.data.data || [];
        setPods(fetchedPods);
        setTotalItems(res.data.count || res.data.total || fetchedPods.length || 0);
      }
    } catch (error) {
      console.error('Error fetching pods data:', error);
    } finally {
      setLoading(false);
    }
  }, [agentId, clientId, currentPage, itemsPerPage, debouncedSearch, statusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = () => {
    fetchData();
  };

  const togglePod = (podId: string) => {
    const newSet = new Set(expandedPods);
    if (newSet.has(podId)) {
      newSet.delete(podId);
    } else {
      newSet.add(podId);
    }
    setExpandedPods(newSet);
  };

  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + pods.length;

  const handlePageChange = (page: number): void => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleItemsPerPageChange = (value: number): void => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  if (loading) return <div className="text-gray-600 dark:text-gray-200 p-4 font-mono text-sm text-center">Loading pods...</div>;

  return (
    <div className="space-y-4">
      {/* Search Bar */}
      <div className="flex flex-col sm:flex-row gap-3 items-center justify-between">
        <div className="text-gray-700 dark:text-gray-200 font-mono text-[11px] bg-white dark:bg-gray-900/50 px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-800 shadow-sm transition-colors duration-200">
          {totalItems > 0 ? `${startIndex + 1} - ${endIndex} of ${totalItems}` : '0 pods'}
        </div>
        <div className="flex gap-2 w-full sm:w-auto items-center">
          <button
            onClick={handleRefresh}
            className="w-10 h-10 flex items-center justify-center rounded-lg bg-white dark:bg-gray-800/80 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:text-orange-500 hover:border-orange-500/50 hover:bg-orange-500/10 transition-all shadow-sm"
            title="Refresh pods"
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          </button>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-200" />
            <input
              type="text"
              placeholder="search pod / node / image…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="bg-white dark:bg-gray-900/50 border border-gray-300 dark:border-gray-700/50 text-gray-900 dark:text-gray-200 text-sm rounded-lg pl-9 pr-4 py-2 w-full sm:w-64 focus:outline-none focus:border-orange-500/50 transition-colors placeholder:text-gray-400 dark:placeholder:text-gray-600 font-mono h-10 shadow-sm"
            />
          </div>
          <CustomDropdown
            data={[
              { label: 'all statuses', value: '' },
              { label: 'problems only', value: 'problem' },
              { label: 'Running', value: 'Running' },
              { label: 'Pending', value: 'Pending' },
              { label: 'Failed', value: 'Failed' },
              { label: 'Succeeded', value: 'Succeeded' }
            ]}
            width="w-44"
            placeholder="all statuses"
            selectedValue={statusFilter}
            onSelect={setStatusFilter}
            className="shadow-sm font-mono"
            buttonHeight={10}
          />
        </div>
      </div>

      <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-3 flex items-center gap-3">
        <Info className="w-4 h-4 text-orange-500 dark:text-orange-400 shrink-0" />
        <span className="text-[13px] text-orange-950 dark:text-orange-200/80 font-sans">
          <strong className="text-orange-600 dark:text-orange-400 font-bold tracking-wide">Tip:</strong> Click any row to expand per-container details and actions.
        </span>
      </div>

      <div className="bg-white dark:bg-gray-800/90 backdrop-blur-md border border-gray-200 dark:border-white/5 rounded-xl mb-3 overflow-hidden shadow-sm dark:shadow-[0_4px_16px_rgba(0,0,0,0.2)] transition-colors duration-200">
        <div className="overflow-x-auto custom-scroll max-h-[72vh]">
          <table className="w-full text-left text-xs border-collapse">
            <thead className="bg-gray-50 dark:bg-gray-800/80 sticky top-0 z-10 backdrop-blur-sm transition-colors duration-200">
              <tr className="border-b border-gray-200 dark:border-gray-700/50">
                <th className="px-2 py-2.5 w-8"></th>
                <th className="px-2 py-2.5 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest">Pod</th>
                <th className="px-2 py-2.5 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest">Namespace</th>
                <th className="px-2 py-2.5 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest text-center">Ready</th>
                <th className="px-2 py-2.5 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest text-center">Status</th>
                <th className="px-2 py-2.5 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest text-center">Restarts</th>
                <th className="px-2 py-2.5 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest text-center">CPU live / req / lim</th>
                <th className="px-2 py-2.5 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest text-center">Memory live / req / lim</th>
                <th className="px-2 py-2.5 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest">Node</th>
                <th className="px-2 py-2.5 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest">IP</th>
                <th className="px-2 py-2.5 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest">Age</th>
              </tr>
            </thead>
            <tbody>
              {pods.length === 0 && !loading ? (
                <tr>
                  <td colSpan={11} className="py-8 px-6 text-center text-gray-500 dark:text-gray-200 font-sans text-[13px] bg-gray-50 dark:bg-black/10 border-b border-gray-200 dark:border-white/5">
                    no pods match
                  </td>
                </tr>
              ) : loading ? (
                <tr>
                  <td colSpan={11} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center space-y-3">
                      <RefreshCw className="w-8 h-8 text-orange-500 animate-spin" />
                      <p className="text-gray-600 dark:text-gray-200 font-medium">Loading pods...</p>
                    </div>
                  </td>
                </tr>
              ) : (
                pods.map((pod: any, idx: number) => {
                  const id = pod.namespace + '/' + (pod.pod_name || pod.name);
                  const isOpen = expandedPods.has(id);

                  let cpu_m = pod.usage?.cpu_m ?? pod.cpu_m ?? pod.cpu_used_m;
                  let cpu_req_m = pod.usage?.cpu_req_m ?? pod.cpu_req_m ?? pod.cpu_request_m;
                  let cpu_lim_m = pod.usage?.cpu_lim_m ?? pod.cpu_lim_m ?? pod.cpu_limit_m;
                  let mem_bytes = pod.usage?.mem_bytes ?? pod.mem_bytes ?? pod.mem_used_bytes;
                  let mem_req_bytes = pod.usage?.mem_req_bytes ?? pod.mem_req_bytes ?? pod.mem_request_bytes;
                  let mem_lim_bytes = pod.usage?.mem_lim_bytes ?? pod.mem_lim_bytes ?? pod.mem_limit_bytes;

                  let cpu_pct_lim = pod.usage?.cpu_pct_lim ?? pod.cpu_pct_lim;
                  let cpu_pct_req = pod.usage?.cpu_pct_req ?? pod.cpu_pct_req;
                  let mem_pct_lim = pod.usage?.mem_pct_lim ?? pod.mem_pct_lim;
                  let mem_pct_req = pod.usage?.mem_pct_req ?? pod.mem_pct_req;

                  if (cpu_pct_lim == null && cpu_m != null && cpu_lim_m) {
                    cpu_pct_lim = (cpu_m / cpu_lim_m) * 100;
                  }
                  if (cpu_pct_req == null && cpu_m != null && cpu_req_m) {
                    cpu_pct_req = (cpu_m / cpu_req_m) * 100;
                  }
                  if (mem_pct_lim == null && mem_bytes != null && mem_lim_bytes) {
                    mem_pct_lim = (mem_bytes / mem_lim_bytes) * 100;
                  }
                  if (mem_pct_req == null && mem_bytes != null && mem_req_bytes) {
                    mem_pct_req = (mem_bytes / mem_req_bytes) * 100;
                  }

                  const u = {
                    cpu_m, cpu_req_m, cpu_lim_m, mem_bytes, mem_req_bytes, mem_lim_bytes,
                    cpu_pct_lim, cpu_pct_req, mem_pct_lim, mem_pct_req
                  };

                  return (
                    <React.Fragment key={idx}>
                      <tr
                        onClick={() => togglePod(id)}
                        className="bg-white dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer group"
                      >
                        <td className="px-2 py-1.5 text-center text-gray-400 dark:text-gray-200 group-hover:text-gray-600 dark:group-hover:text-gray-300">
                          {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </td>
                        <td className="px-2 py-1.5 font-mono text-gray-900 dark:text-white font-bold whitespace-nowrap">{pod.pod_name || pod.name}</td>
                        <td className="px-2 py-1.5 text-gray-600 dark:text-gray-200 font-mono font-semibold text-[11px]">{pod.namespace}</td>
                        <td className="px-2 py-1.5 text-center text-gray-700 dark:text-gray-100 font-semibold">{pod.ready}</td>
                        <td className="px-2 py-1.5 text-center font-mono">
                          <span className={`text-[9px] px-1.5 py-0.5 rounded border font-bold whitespace-nowrap ${(pod.status || pod.phase) === 'Running' || (pod.status || pod.phase) === 'Succeeded'
                            ? 'border-blue-500/30 bg-blue-500/10 text-blue-600 dark:text-blue-400'
                            : 'border-orange-500/30 bg-orange-500/10 text-orange-600 dark:text-orange-400'
                            }`}>
                            {pod.status || pod.phase}
                          </span>
                        </td>
                        <td className="px-2 py-1.5 text-center font-mono font-bold">
                          <span className={`${pod.restarts > 10 ? "text-red-500 dark:text-red-400" : pod.restarts > 5 ? "text-amber-500 dark:text-yellow-400" : "text-gray-600 dark:text-gray-300"}`}>
                            {pod.restarts}
                          </span>
                        </td>

                        <td className="px-2 py-1.5">
                          <div className="flex flex-col items-center">
                            {u.cpu_pct_lim != null || u.cpu_pct_req != null ? (
                              <div className="w-24 h-1.5 bg-gray-200 dark:bg-gray-900 rounded-full overflow-hidden border border-gray-300 dark:border-white/5 mb-1 shadow-inner">
                                <div className="h-full bg-gradient-to-r from-orange-500 to-amber-400" style={{ width: `${Math.min(100, u.cpu_pct_lim ?? u.cpu_pct_req)}%` }}></div>
                              </div>
                            ) : (
                              <span className="text-[9px] text-gray-500 dark:text-gray-200 font-mono mb-0.5 font-semibold">no limits set</span>
                            )}
                            <span className="text-[10px] text-gray-700 dark:text-gray-200 font-mono font-bold">
                              {fmtCpu(u.cpu_m)} / {fmtCpu(u.cpu_req_m)} / {fmtCpu(u.cpu_lim_m)}
                            </span>
                          </div>
                        </td>

                        <td className="px-2 py-1.5">
                          <div className="flex flex-col items-center">
                            {u.mem_pct_lim != null || u.mem_pct_req != null ? (
                              <div className="w-24 h-1.5 bg-gray-200 dark:bg-gray-900 rounded-full overflow-hidden border border-gray-300 dark:border-white/5 mb-1 shadow-inner">
                                <div className="h-full bg-gradient-to-r from-emerald-500 to-teal-400" style={{ width: `${Math.min(100, u.mem_pct_lim ?? u.mem_pct_req)}%` }}></div>
                              </div>
                            ) : (
                              <span className="text-[9px] text-gray-500 dark:text-gray-200 font-mono mb-0.5 font-semibold">no limits set</span>
                            )}
                            <span className="text-[10px] text-gray-700 dark:text-gray-200 font-mono font-bold">
                              {fmtBytes(u.mem_bytes)} / {fmtBytes(u.mem_req_bytes)} / {fmtBytes(u.mem_lim_bytes)}
                            </span>
                          </div>
                        </td>

                        <td className="px-2 py-1.5 text-gray-600 dark:text-gray-200 font-mono font-semibold text-[11px] whitespace-nowrap">{pod.node_name || pod.node}</td>
                        <td className="px-2 py-1.5 text-gray-600 dark:text-gray-200 font-mono font-semibold text-[11px]">{pod.pod_ip}</td>
                        <td className="px-2 py-1.5 text-gray-600 dark:text-gray-200 font-mono font-semibold text-[11px] whitespace-nowrap">{pod.age || '—'}</td>
                      </tr>

                      {isOpen && (
                        <tr className="bg-gray-50/70 dark:bg-black/20 border-b border-gray-200 dark:border-gray-700/50">
                          <td colSpan={11} className="p-0">
                            <div className="p-4 pl-8 border-l-2 border-orange-500/50 bg-gradient-to-r from-orange-500/5 to-transparent">
                              <div className="flex items-center gap-4 mb-4">
                                <div className="flex items-center gap-2">
                                  <span className="text-[10px] uppercase tracking-widest text-gray-700 dark:text-gray-200 font-bold">Pod Actions</span>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setDescribePod(pod); }}
                                    className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold font-mono bg-white dark:bg-gray-800/80 border border-gray-300 dark:border-gray-600/50 rounded-md text-gray-700 dark:text-gray-300 hover:text-orange-500 hover:border-orange-500/50 hover:bg-orange-500/10 transition-all shadow-sm"
                                  >
                                    <FileText size={14} />
                                    describe
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/kubernetes/dashboard/${agentId}?client_id=${clientId}&tab=history&involved_object_uid=${pod.pod_uid}`);
                                    }}
                                    className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold font-mono bg-white dark:bg-gray-800/80 border border-gray-300 dark:border-gray-600/50 rounded-md text-gray-700 dark:text-gray-300 hover:text-orange-500 hover:border-orange-500/50 hover:bg-orange-500/10 transition-all shadow-sm"
                                  >
                                    <Clock size={14} />
                                    history ↗
                                  </button>
                                </div>

                                {(() => {
                                  const containersList = pod.containers || (pod.container_names ? pod.container_names.map((name: string) => ({ name })) : []);
                                  return containersList.length > 0 && (
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="text-[10px] uppercase tracking-widest text-gray-700 dark:text-gray-200 font-bold ml-2">Containers</span>
                                      {containersList.map((ct: any, cidx: number) => {
                                        const isShellEnabled = pod.shell_enabled === true || pod.shell_enabled === 'true';
                                        const isShellDisabled = pod.shell_enabled === false || pod.shell_enabled === 'false';

                                        let shellButtonClass = "flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold font-mono transition-all shadow-sm rounded-md ";
                                        if (isShellEnabled) {
                                          shellButtonClass += "bg-green-500/10 border border-green-500/30 text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 hover:border-green-500/50 hover:bg-green-500/20";
                                        } else if (isShellDisabled) {
                                          shellButtonClass += "bg-gray-100 dark:bg-gray-800/80 border border-gray-300 dark:border-gray-600/50 text-orange-600 dark:text-orange-400 hover:border-orange-500/30 hover:bg-orange-500/5";
                                        } else {
                                          shellButtonClass += "bg-white dark:bg-gray-800/80 border border-gray-300 dark:border-gray-600/50 text-gray-700 dark:text-gray-300 hover:text-orange-500 hover:border-orange-500/50 hover:bg-orange-500/10";
                                        }

                                        return (
                                          <React.Fragment key={cidx}>
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setLogsConfig({
                                                  podUid: pod.pod_uid,
                                                  podName: pod.pod_name || pod.name,
                                                  containerName: ct.name
                                                });
                                              }}
                                              className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold font-mono bg-white dark:bg-gray-800/80 border border-gray-300 dark:border-gray-600/50 rounded-md text-gray-700 dark:text-gray-300 hover:text-orange-500 hover:border-orange-500/50 hover:bg-orange-500/10 transition-all shadow-sm"
                                            >
                                              <AlignLeft size={14} />
                                              logs: {ct.name}
                                            </button>
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                const phase = (pod.phase || pod.status || '').toLowerCase();
                                                if (isShellEnabled) {
                                                  setShellConfig({
                                                    namespace: pod.namespace,
                                                    podName: pod.pod_name || pod.name,
                                                    containerName: ct.name
                                                  });
                                                } else {
                                                  if (phase === 'running') {
                                                    showError("No shell available (distroless image)");
                                                  } else {
                                                    showError("Pod is not running");
                                                  }
                                                }
                                              }}
                                              className={shellButtonClass}
                                            >
                                              <Terminal size={14} />
                                              shell: {ct.name}
                                            </button>
                                          </React.Fragment>
                                        );
                                      })}
                                    </div>
                                  );
                                })()}
                              </div>

                              {(!pod.containers || pod.containers.length === 0) ? (
                                (pod.status === 'Running' || pod.phase === 'Running' || pod.status === 'Succeeded' || pod.phase === 'Succeeded') ? (
                                  <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded font-mono text-[12px]">
                                    <span className="px-1.5 py-0.5 bg-blue-500/20 text-blue-400 font-bold rounded mr-3 text-[10px]">
                                      {(pod.status === 'Succeeded' || pod.phase === 'Succeeded') ? 'COMPLETED' : 'RUNNING'}
                                    </span>
                                    <span className="text-gray-300">
                                      {pod.container_count ? `${pod.container_count} container(s) active` : 'Container details not available in this view'}
                                    </span>
                                  </div>
                                ) : (
                                  <div className="p-3 bg-red-500/10 border border-red-500/20 rounded font-mono text-[12px]">
                                    <span className="px-1.5 py-0.5 bg-red-500/20 text-red-400 font-bold rounded mr-3 text-[10px]">NO CONTAINERS STARTED</span>
                                    <span className="text-gray-300">{pod.pending_reason || pod.status || pod.phase || "waiting for scheduling / image pull"}</span>
                                    <div className="text-gray-200 mt-2 text-[11px]">tip: kubectl describe pod {pod.pod_name || pod.name} -n {pod.namespace} — check Events for scheduler/autoscaler messages</div>
                                  </div>
                                )
                              ) : (
                                <table className="w-full text-left text-[11px] font-mono mt-2 bg-white dark:bg-gray-900/50 rounded-lg overflow-hidden border border-gray-200 dark:border-white/5 shadow-sm">
                                  <thead className="bg-gray-100 dark:bg-black/40">
                                    <tr>
                                      <th className="px-3 py-2 text-gray-700 dark:text-gray-200 font-normal border-b border-gray-200 dark:border-white/5">Container</th>
                                      <th className="px-3 py-2 text-gray-700 dark:text-gray-200 font-normal border-b border-gray-200 dark:border-white/5">Image</th>
                                      <th className="px-3 py-2 text-gray-700 dark:text-gray-200 font-normal border-b border-gray-200 dark:border-white/5">State</th>
                                      <th className="px-3 py-2 text-gray-700 dark:text-gray-200 font-normal border-b border-gray-200 dark:border-white/5 text-center">Restarts</th>
                                      <th className="px-3 py-2 text-gray-700 dark:text-gray-200 font-normal border-b border-gray-200 dark:border-white/5 text-center">CPU live</th>
                                      <th className="px-3 py-2 text-gray-700 dark:text-gray-200 font-normal border-b border-gray-200 dark:border-white/5 text-center">Memory live</th>
                                      <th className="px-3 py-2 text-gray-700 dark:text-gray-200 font-normal border-b border-gray-200 dark:border-white/5">Last term.</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {pod.containers.map((ct: any, cidx: number) => {
                                      const hasExtraDetails =
                                        (ct.ports && ct.ports.length > 0) ||
                                        (ct.env_count != null && ct.env_count > 0) ||
                                        ct.liveness ||
                                        ct.readiness ||
                                        ct.requests ||
                                        ct.limits ||
                                        (ct.mounts && ct.mounts.length > 0);

                                      return (
                                        <React.Fragment key={cidx}>
                                          <tr className={`border-b border-gray-200 dark:border-white/5 ${hasExtraDetails ? '' : 'last:border-0'} hover:bg-gray-50 dark:hover:bg-white/5 transition-colors`}>
                                            <td className="px-3 py-2 text-gray-900 dark:text-gray-200 font-bold">{ct.name}</td>
                                            <td className="px-3 py-2 text-gray-700 dark:text-gray-200 break-all max-w-[200px]">{ct.image}</td>
                                            <td className="px-3 py-2">
                                              {ct.reason ? (
                                                <span className="px-1.5 py-0.5 bg-red-500/10 text-red-600 dark:text-red-400 rounded text-[9px] font-bold">{ct.reason}</span>
                                              ) : (
                                                <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${ct.ready ? 'bg-green-500/20 text-green-600 dark:text-green-400' : 'bg-yellow-500/20 text-yellow-600 dark:text-yellow-400'}`}>
                                                  {ct.state || 'Unknown'}
                                                </span>
                                              )}
                                            </td>
                                            <td className="px-3 py-2 text-gray-700 dark:text-gray-200 text-center">{ct.restarts || 0}</td>
                                            <td className="px-3 py-2 text-gray-700 dark:text-gray-200 text-center">{fmtCpu(ct.usage?.cpu_m ?? ct.cpu_m ?? ct.cpu_used_m)}</td>
                                            <td className="px-3 py-2 text-gray-700 dark:text-gray-200 text-center">{fmtBytes(ct.usage?.mem_bytes ?? ct.mem_bytes ?? ct.mem_used_bytes)}</td>
                                            <td className={`px-3 py-2 ${ct.last_terminated === 'OOMKilled' ? 'text-red-500 dark:text-red-400 font-bold' : 'text-gray-600 dark:text-gray-200'}`}>
                                              {ct.last_terminated || '—'}
                                            </td>
                                          </tr>
                                          {hasExtraDetails && (
                                            <tr className="border-b border-gray-200 dark:border-white/5 last:border-0 bg-gray-50/80 dark:bg-black/10 text-[10px]">
                                              <td colSpan={7} className="px-4 py-2 text-gray-700 dark:text-gray-200">
                                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2 font-sans py-1">
                                                  {ct.ports && ct.ports.length > 0 && (
                                                    <div className="flex flex-col">
                                                      <span className="text-gray-500 dark:text-gray-200 font-mono font-bold text-[9px] uppercase tracking-wider">Ports</span>
                                                      <span className="text-gray-800 dark:text-gray-300 font-mono mt-0.5">{ct.ports.join(', ')}</span>
                                                    </div>
                                                  )}
                                                  {ct.env_count != null && ct.env_count > 0 && (
                                                    <div className="flex flex-col">
                                                      <span className="text-gray-500 dark:text-gray-200 font-mono font-bold text-[9px] uppercase tracking-wider">Environment Variables</span>
                                                      <span className="text-gray-800 dark:text-gray-300 font-mono mt-0.5">{ct.env_count} vars</span>
                                                    </div>
                                                  )}
                                                  {(ct.requests || ct.limits) && (
                                                    <div className="flex flex-col">
                                                      <span className="text-gray-500 dark:text-gray-200 font-mono font-bold text-[9px] uppercase tracking-wider">Req / Lim</span>
                                                      <span className="text-gray-800 dark:text-gray-300 font-mono mt-0.5">
                                                        Req: {ct.requests?.cpu ? `cpu:${ct.requests.cpu}` : 'cpu:—'}, {ct.requests?.memory ? `mem:${ct.requests.memory}` : 'mem:—'} | Lim: {ct.limits?.cpu ? `cpu:${ct.limits.cpu}` : 'cpu:—'}, {ct.limits?.memory ? `mem:${ct.limits.memory}` : 'mem:—'}
                                                      </span>
                                                    </div>
                                                  )}
                                                  {ct.liveness && (
                                                    <div className="flex flex-col">
                                                      <span className="text-gray-500 dark:text-gray-200 font-mono font-bold text-[9px] uppercase tracking-wider">Liveness Probe</span>
                                                      <span className="text-gray-800 dark:text-gray-300 font-mono mt-0.5 text-[9.5px] leading-tight">{ct.liveness}</span>
                                                    </div>
                                                  )}
                                                  {ct.readiness && (
                                                    <div className="flex flex-col">
                                                      <span className="text-gray-500 dark:text-gray-200 font-mono font-bold text-[9px] uppercase tracking-wider">Readiness Probe</span>
                                                      <span className="text-gray-800 dark:text-gray-300 font-mono mt-0.5 text-[9.5px] leading-tight">{ct.readiness}</span>
                                                    </div>
                                                  )}
                                                  {ct.mounts && ct.mounts.length > 0 && (
                                                    <div className="flex flex-col col-span-1 md:col-span-2 lg:col-span-3 mt-1 border-t border-gray-200 dark:border-white/5 pt-1.5">
                                                      <span className="text-gray-500 dark:text-gray-200 font-mono font-bold text-[9px] uppercase tracking-wider">Volume Mounts</span>
                                                      <ul className="list-disc pl-4 text-gray-800 dark:text-gray-300 font-mono text-[9px] mt-1 space-y-0.5 leading-normal">
                                                        {ct.mounts.map((mnt: string, midx: number) => (
                                                          <li key={midx}>{mnt}</li>
                                                        ))}
                                                      </ul>
                                                    </div>
                                                  )}
                                                </div>
                                              </td>
                                            </tr>
                                          )}
                                        </React.Fragment>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
          {/* Table Footer / Pagination */}
          <div className="bg-gray-50 dark:bg-gray-800/80 border-t border-gray-200 dark:border-gray-700 px-3 py-2 flex items-center justify-between sticky bottom-0 z-10 backdrop-blur-sm transition-colors duration-200">
            <div className="flex items-center gap-4">
              <ItemsPerPageDropdown
                value={itemsPerPage}
                onChange={(val: any) => handleItemsPerPageChange(val)}
              />
              {totalItems > 0 ? (
                <span className="text-[10px] text-gray-700 dark:text-gray-200 font-bold uppercase tracking-widest font-mono">
                  <span>{startIndex + 1}</span> - <span>{endIndex}</span> OF {totalItems}
                </span>
              ) : (
                pods.length > 0 && (
                  <span className="text-[10px] text-gray-700 dark:text-gray-200 font-bold uppercase tracking-widest font-mono">
                    <span>{startIndex + 1}</span> - <span>{startIndex + pods.length}</span>
                  </span>
                )
              )}
            </div>
            <div className="flex items-center gap-1 font-mono">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                className="p-1 text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white disabled:opacity-30 transition-colors"
                title="First Page"
              >
                <ChevronsLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-1 text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white disabled:opacity-30 transition-colors"
                title="Previous Page"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <div className="px-2 py-0.5 bg-orange-600 rounded text-white text-[10px] font-bold mx-1 shadow-md shadow-orange-950/20">
                {currentPage}
              </div>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={pods.length < itemsPerPage || (totalItems > 0 && currentPage === totalPages)}
                className="p-1 text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white disabled:opacity-30 transition-colors"
                title="Next Page"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              {totalItems > 0 && (
                <button
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                  className="p-1 text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white disabled:opacity-30 transition-colors"
                  title="Last Page"
                >
                  <ChevronsRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Describe Modal */}
      {describePod && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setDescribePod(null)}>
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl w-full max-w-4xl overflow-hidden flex flex-col transition-colors duration-200" onClick={e => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="flex justify-between items-center px-4 py-3 border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-black/20">
              <h3 className="text-gray-700 dark:text-gray-100 font-mono text-sm">
                describe — <span className="font-bold text-gray-900 dark:text-white">{describePod.name || describePod.pod_name}</span> <span className="text-gray-500 dark:text-gray-400">({describePod.namespace})</span>
              </h3>
              <button onClick={() => setDescribePod(null)} className="text-gray-400 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white transition-colors">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-4 overflow-y-auto max-h-[80vh] custom-scroll space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Pod Info Grid */}
                <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-white/5 rounded-lg p-3 shadow-sm">
                  <h4 className="text-[10px] uppercase tracking-widest text-gray-700 dark:text-gray-200 font-bold mb-3">Pod Info</h4>
                  <div className="grid grid-cols-[110px_1fr] gap-y-2 text-[11px] font-mono">
                    <span className="text-gray-500 dark:text-gray-400">name</span><span className="text-gray-800 dark:text-gray-200 truncate" title={describePod.name || describePod.pod_name}>{describePod.name || describePod.pod_name}</span>
                    <span className="text-gray-500 dark:text-gray-400">namespace</span><span className="text-gray-800 dark:text-gray-200">{describePod.namespace}</span>
                    <span className="text-gray-500 dark:text-gray-400">phase</span><span className="text-gray-800 dark:text-gray-200">{describePod.phase}</span>
                    <span className="text-gray-500 dark:text-gray-400">status</span><span className="text-gray-800 dark:text-gray-200">{describePod.status}</span>
                    <span className="text-gray-500 dark:text-gray-400">node</span><span className="text-gray-800 dark:text-gray-200">{describePod.node || describePod.node_name}</span>
                    <span className="text-gray-500 dark:text-gray-400">host IP</span><span className="text-gray-800 dark:text-gray-200">{describePod.host_ip || '—'}</span>
                    <span className="text-gray-500 dark:text-gray-400">pod IP</span><span className="text-gray-800 dark:text-gray-200">{describePod.pod_ip || '—'}</span>
                    <span className="text-gray-500 dark:text-gray-400">ready</span><span className="text-gray-800 dark:text-gray-200">{describePod.ready}</span>
                    <span className="text-gray-500 dark:text-gray-400">restarts</span><span className="text-gray-800 dark:text-gray-200">{describePod.restarts}</span>
                    <span className="text-gray-500 dark:text-gray-400">QoS</span><span className="text-gray-800 dark:text-gray-200">{describePod.qos_class || '—'}</span>
                    <span className="text-gray-500 dark:text-gray-400">owner</span><span className="text-gray-800 dark:text-gray-200">{describePod.owner_kind || '—'}</span>
                    <span className="text-gray-500 dark:text-gray-400">service account</span><span className="text-gray-800 dark:text-gray-200">{describePod.service_account || '—'}</span>
                    <span className="text-gray-500 dark:text-gray-400">restart policy</span><span className="text-gray-800 dark:text-gray-200">{describePod.restart_policy || '—'}</span>
                    <span className="text-gray-500 dark:text-gray-400">priority</span><span className="text-gray-800 dark:text-gray-200">{describePod.priority != null ? describePod.priority : '—'}</span>
                    <span className="text-gray-500 dark:text-gray-400">start time</span><span className="text-gray-800 dark:text-gray-200">{describePod.start_time ? new Date(describePod.start_time).toLocaleString() : '—'}</span>
                    <span className="text-gray-500 dark:text-gray-400">age</span><span className="text-gray-800 dark:text-gray-200">{describePod.age || '—'}</span>
                  </div>
                </div>

                {/* Resource Usage Grid */}
                <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-white/5 rounded-lg p-3 shadow-sm">
                  <h4 className="text-[10px] uppercase tracking-widest text-gray-700 dark:text-gray-200 font-bold mb-3">Resource Usage</h4>
                  <div className="grid grid-cols-[120px_1fr] gap-y-2 text-[11px] font-mono">
                    <span className="text-gray-500 dark:text-gray-400">CPU used</span><span className="text-gray-800 dark:text-gray-200">{fmtCpu(describePod.usage?.cpu_m ?? describePod.cpu_m ?? describePod.cpu_used_m)}</span>
                    <span className="text-gray-500 dark:text-gray-400">CPU request</span><span className="text-gray-800 dark:text-gray-200">{fmtCpu(describePod.usage?.cpu_req_m ?? describePod.cpu_req_m ?? describePod.cpu_request_m)}</span>
                    <span className="text-gray-500 dark:text-gray-400">CPU limit</span><span className="text-gray-800 dark:text-gray-200">{fmtCpu(describePod.usage?.cpu_lim_m ?? describePod.cpu_lim_m ?? describePod.cpu_limit_m)}</span>
                    <span className="text-gray-500 dark:text-gray-400">Memory used</span><span className="text-gray-800 dark:text-gray-200">{fmtBytes(describePod.usage?.mem_bytes ?? describePod.mem_bytes ?? describePod.mem_used_bytes)}</span>
                    <span className="text-gray-500 dark:text-gray-400">Memory req</span><span className="text-gray-800 dark:text-gray-200">{fmtBytes(describePod.usage?.mem_req_bytes ?? describePod.mem_req_bytes ?? describePod.mem_request_bytes)}</span>
                    <span className="text-gray-500 dark:text-gray-400">Memory limit</span><span className="text-gray-800 dark:text-gray-200">{fmtBytes(describePod.usage?.mem_lim_bytes ?? describePod.mem_lim_bytes ?? describePod.mem_limit_bytes)}</span>
                  </div>
                </div>
              </div>

              {/* Labels, Selectors & Annotations */}
              {((describePod.labels && describePod.labels.length > 0) ||
                (describePod.node_selector && describePod.node_selector.length > 0) ||
                (describePod.annotations && describePod.annotations.length > 0)) && (
                  <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-white/5 rounded-lg p-3 space-y-3 shadow-sm">
                    <h4 className="text-[10px] uppercase tracking-widest text-gray-700 dark:text-gray-200 font-bold">Labels, Selectors & Annotations</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-3">
                        {describePod.labels && describePod.labels.length > 0 && (
                          <div>
                            <h5 className="text-[9px] uppercase tracking-wider text-gray-500 dark:text-gray-400 font-bold mb-1.5">Labels</h5>
                            <div className="flex flex-wrap gap-1">
                              {describePod.labels.map((lbl: string, lidx: number) => (
                                <span key={lidx} className="px-2 py-0.5 bg-blue-500/10 border border-blue-500/20 text-blue-600 dark:text-blue-400 rounded text-[10px] font-mono leading-tight font-medium">
                                  {lbl}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {describePod.node_selector && describePod.node_selector.length > 0 && (
                          <div>
                            <h5 className="text-[9px] uppercase tracking-wider text-gray-500 dark:text-gray-400 font-bold mb-1.5">Node Selectors</h5>
                            <div className="flex flex-wrap gap-1">
                              {describePod.node_selector.map((sel: string, sidx: number) => (
                                <span key={sidx} className="px-2 py-0.5 bg-teal-500/10 border border-teal-500/20 text-teal-600 dark:text-teal-400 rounded text-[10px] font-mono leading-tight font-medium">
                                  {sel}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      {describePod.annotations && describePod.annotations.length > 0 && (
                        <div>
                          <h5 className="text-[9px] uppercase tracking-wider text-gray-500 dark:text-gray-400 font-bold mb-1.5">Annotations</h5>
                          <div className="space-y-1.5 max-h-36 overflow-y-auto custom-scroll pr-1">
                            {describePod.annotations.map((ann: string, aidx: number) => (
                              <div key={aidx} className="px-2 py-1 bg-white dark:bg-black/30 border border-gray-200 dark:border-white/5 rounded text-[10px] text-gray-700 dark:text-gray-300 font-mono break-all leading-normal">
                                {ann}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

              {/* Conditions */}
              {describePod.conditions && describePod.conditions.length > 0 && (
                <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-white/5 rounded-lg p-3 shadow-sm">
                  <h4 className="text-[10px] uppercase tracking-widest text-gray-700 dark:text-gray-200 font-bold mb-2">Conditions</h4>
                  <table className="w-full text-left text-[11px] font-mono border-collapse">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-white/5 bg-gray-100 dark:bg-black/10">
                        <th className="px-2 py-1.5 text-gray-700 dark:text-gray-200 font-semibold">Type</th>
                        <th className="px-2 py-1.5 text-gray-700 dark:text-gray-200 font-semibold text-center">Status</th>
                        <th className="px-2 py-1.5 text-gray-700 dark:text-gray-200 font-semibold">Reason</th>
                        <th className="px-2 py-1.5 text-gray-700 dark:text-gray-200 font-semibold">Message</th>
                      </tr>
                    </thead>
                    <tbody>
                      {describePod.conditions.map((c: any, cidx: number) => (
                        <tr key={cidx} className="border-b border-gray-200 dark:border-white/5 last:border-0 hover:bg-gray-100/50 dark:hover:bg-white/5 transition-colors">
                          <td className="px-2 py-1.5 text-gray-900 dark:text-gray-200 font-bold">{c.type}</td>
                          <td className="px-2 py-1.5 text-center">
                            <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${c.status === 'True' ? 'bg-green-500/20 text-green-600 dark:text-green-400' : 'bg-red-500/20 text-red-600 dark:text-red-400'}`}>
                              {c.status}
                            </span>
                          </td>
                          <td className="px-2 py-1.5 text-gray-600 dark:text-gray-300">{c.reason || '—'}</td>
                          <td className="px-2 py-1.5 text-gray-700 dark:text-gray-200 break-words max-w-xs">{c.message || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Volumes & Tolerations */}
              {((describePod.volumes && describePod.volumes.length > 0) ||
                (describePod.tolerations && describePod.tolerations.length > 0)) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {describePod.volumes && describePod.volumes.length > 0 ? (
                      <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-white/5 rounded-lg p-3 shadow-sm">
                        <h4 className="text-[10px] uppercase tracking-widest text-gray-700 dark:text-gray-200 font-bold mb-2">Volumes</h4>
                        <div className="space-y-2 max-h-48 overflow-y-auto custom-scroll pr-1">
                          {describePod.volumes.map((v: any, vidx: number) => (
                            <div key={vidx} className="text-[11px] font-mono border-b border-gray-200 dark:border-white/5 pb-1.5 last:border-0 last:pb-0">
                              <div className="text-gray-900 dark:text-gray-200 font-bold">{v.name}</div>
                              <div className="text-gray-600 dark:text-gray-300 text-[10px] mt-0.5">
                                Type: <span className="text-orange-600 dark:text-orange-400">{v.type}</span>
                                {v.detail && <span className="text-gray-500 dark:text-gray-200"> ({v.detail})</span>}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : <div />}

                    {describePod.tolerations && describePod.tolerations.length > 0 ? (
                      <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-white/5 rounded-lg p-3 shadow-sm">
                        <h4 className="text-[10px] uppercase tracking-widest text-gray-700 dark:text-gray-200 font-bold mb-2">Tolerations</h4>
                        <div className="flex flex-wrap gap-1.5 max-h-48 overflow-y-auto custom-scroll pr-1">
                          {describePod.tolerations.map((t: string, tidx: number) => (
                            <span key={tidx} className="px-2 py-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-white/5 rounded text-[10px] text-gray-700 dark:text-gray-300 font-mono leading-tight whitespace-nowrap shadow-sm">
                              {t}
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : <div />}
                  </div>
                )}

              <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-3 mt-2">
                <h4 className="text-[10px] uppercase tracking-widest text-orange-600 dark:text-orange-500 font-bold mb-1">Note</h4>
                <p className="text-[11px] font-mono text-orange-900 dark:text-orange-400/80 leading-relaxed">
                  Full pod description requires direct Kubernetes API access.<br />
                  For complete details, use: <code className="bg-black/10 dark:bg-black/30 px-1.5 py-0.5 rounded text-orange-700 dark:text-orange-300">kubectl describe pod {describePod.name || describePod.pod_name} -n {describePod.namespace}</code>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Shell Modal */}
      {shellConfig && (
        <K8sPodShellModal
          agentId={agentId}
          clientId={clientId}
          namespace={shellConfig.namespace}
          podName={shellConfig.podName}
          containerName={shellConfig.containerName}
          onClose={() => setShellConfig(null)}
        />
      )}

      {/* Logs Modal */}
      {logsConfig && (
        <K8sPodLogsModal
          agentId={agentId}
          clientId={clientId}
          podUid={logsConfig.podUid}
          podName={logsConfig.podName}
          initialContainer={logsConfig.containerName}
          onClose={() => setLogsConfig(null)}
        />
      )}
    </div>
  );
};

export default PodsTab;
